import rehypeParse from "rehype-parse";
import rehypeRemark from "rehype-remark";
import remarkGfm from "remark-gfm";
import remarkStringify from "remark-stringify";
import { unified } from "unified";

const FENCE_LINE_REGEX = /^\s*```/;

/**
 * Split content into fenced code segments and the prose between them. A fence
 * starts and ends on a line whose first non-whitespace characters are ```, so
 * a triple-backtick appearing *inside* a line (e.g., in a Python string) does
 * not close the fence prematurely. An unclosed fence (e.g., during streaming)
 * is treated as code that runs to end-of-string.
 */
const splitByCodeFences = (
  content: string,
): Array<{ type: "text" | "code"; value: string }> => {
  const parts: Array<{ type: "text" | "code"; value: string }> = [];
  const lines = content.split("\n");
  let buf: string[] = [];
  let bufType: "text" | "code" = "text";
  let inFence = false;

  const flush = () => {
    if (buf.length > 0) {
      parts.push({ type: bufType, value: buf.join("\n") });
      buf = [];
    }
  };

  for (const line of lines) {
    if (FENCE_LINE_REGEX.test(line)) {
      if (inFence) {
        // Closing fence — include this line in the code segment, then flush.
        buf.push(line);
        flush();
        bufType = "text";
        inFence = false;
      } else {
        // Opening fence — flush accumulated prose, start a new code segment
        // with this line at its head.
        flush();
        bufType = "code";
        buf.push(line);
        inFence = true;
      }
    } else {
      buf.push(line);
    }
  }
  flush();
  return parts;
};

/**
 * Insert a non-breaking-space paragraph between runs of three-or-more
 * newlines so the visual gap survives commonmark's paragraph-merge.
 *
 * CommonMark collapses any run of empty lines into a single paragraph
 * break, which means a user-typed `\n\n\n` (deliberate blank line) renders
 * the same as `\n\n`. Inserting `&nbsp;\n ` between the second and third
 * newlines splits the run into two paragraph breaks around an invisible
 * paragraph, so the gap renders as authored.
 *
 * Only used by surfaces with hand-typed content (Comments and similar).
 * AI-generated markdown does not need this — the AgentThread renderer skips it.
 */
export const preserveBlankLines = (content: string): string => {
  // Skip fenced code so the substitution doesn't leak `&nbsp;` into Python
  // / SQL source the user pastes verbatim.
  return splitByCodeFences(content)
    .map((part) =>
      part.type === "code"
        ? part.value
        : part.value.replace(/(?:\n\n)(?![*-])\n/gi, "&nbsp;\n "),
    )
    .join("\n");
};

export const convertToMarkdown = (html: string): string => {
  const markdown = unified()
    .use(rehypeParse, { fragment: true }) // Parse HTML
    .use(rehypeRemark) // Convert to Markdown AST
    .use(remarkGfm) // Support GFM extensions (tables, strikethrough, etc.)
    .use(remarkStringify) // Convert AST to Markdown string
    .processSync(html)
    .toString();

  // Remark stringify writes mention links as `[@name](/users/<id>)`; restore
  // the user-authored `@[name](id)` shape so the clipboard round-trips
  // back into the editor without surprise href changes.
  return markdown.replace(/\[@(.*?)\]\(\/users\/(.*?)\)/g, "@[$1]($2)").trim();
};
